// Üzenet-címzett TÖBB-választós picker — a compose-form célzott dimenziójához (részlegek / kollégák /
// fiókok / kontaktok / szolgáltatások). Az EntityCombobox vizuális mintáját követi (Popover + kereső +
// lista), de SZINKRON opció-listával (a /messages/refs kis referencialistái) és checkbox-os több-választással.
// Kontrollált: `value` az id-tömb, `onChange(ids)`. A kiválasztás a popover zárása NÉLKÜL bővíthető.
// Feature-lokális komponens (csak a messages compose használja) — 2. fogyasztónál promotálandó shared-be.
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { CheckboxVisual } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface TargetOption {
  id: string;
  name: string;
}

export interface MessageTargetMultiSelectProps {
  options: TargetOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
  'aria-label'?: string | undefined;
}

export function MessageTargetMultiSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  'aria-label': ariaLabel,
}: MessageTargetMultiSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // ZÁRÁSKOR a keresőszöveg ürül: különben az újranyitott lista a régi szűréssel nyílt, és úgy tűnt, a többi
  // elem eltűnt (kolléga-észrevétel). Nyitva tartott többválasztásnál viszont MARAD — egy keresésre több
  // találat is egymás után kipipálható.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const selected = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return q === '' ? options : options.filter((o) => o.name.toLocaleLowerCase().includes(q));
  }, [options, query]);

  // A trigger felirata: a kiválasztott nevek (a lista sorrendjében), truncate-tel — üresen a placeholder.
  const selectedNames = useMemo(
    () => options.filter((o) => selected.has(o.id)).map((o) => o.name),
    [options, selected],
  );
  const triggerLabel =
    selectedNames.length === 0
      ? (placeholder ?? t('messages.compose.targetsPlaceholder'))
      : selectedNames.join(', ');

  const toggle = (id: string): void => {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="flex items-center gap-1">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={ariaLabel}
            className="min-w-0 flex-1 justify-between font-normal"
          >
            <span className={cn('truncate', selectedNames.length === 0 && 'text-muted-foreground')}>
              {triggerLabel}
            </span>
            <span className="ml-2 flex shrink-0 items-center gap-1.5">
              {selectedNames.length > 0 && (
                <span className="text-muted-foreground text-xs tabular-nums">
                  {t('messages.compose.targetsCount', { count: selectedNames.length })}
                </span>
              )}
              <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        {value.length > 0 && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common.clear')}
            onClick={() => onChange([])}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
      {/* portal={false}: modális Sheet/Dialog belsejében a lista GÖRGETHETŐ maradjon. A body-szintű portál a
          Dialog scroll-lockja alá esik, ami a wheel-eseményt elnyeli — mérve: a lista 1520px tartalmat hordozott
          256px ablakban, és a görgetés SEMMIT nem mozdított. (Ugyanez a megoldás az `EntityCombobox`-ban.) */}
      <PopoverContent align="start" portal={false} className="w-72 p-0">
        {/* A kereső ALATT NINCS elválasztó (rendszer-egységes — lásd `EntityCombobox`/`EntityMultiSelect`). */}
        <div className="p-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // ENTER a keresőben NEM küldi be a környező űrlapot (a kereső a sheet `<form>`-jában ül —
            // kolléga-észrevétel), és nem is pipál: több-választásnál egy véletlen Enter nem kívánt elemet jelölne
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder={t('common.searchPlaceholder')}
            aria-label={t('common.searchPlaceholder')}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.loading')}</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.noResults')}</p>
          ) : (
            filtered.map((opt) => {
              const checked = selected.has(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(opt.id)}
                  className="hover:bg-accent flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
                >
                  {/* A checkbox csak vizuális jelző — a kattintást a sor-gomb kezeli (nagy találati felület). */}
                  {/* CSAK jelző (nem vezérlő): a kattintást a sor-gomb kezeli. Interaktív Radix-checkbox itt
                      `<button>`-t ültetett `<button>`-be — érvénytelen DOM, és végtelen render-ciklust okozott. */}
                  <CheckboxVisual checked={checked} />
                  <span className="truncate">{opt.name}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
